import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import permissionService from '../../services/permissionService';
import employeeService from '../../services/employeeService';
import MasterPageWrapper from './common/MasterPageWrapper';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import './RolePermissionPage.css';

export const RolePermissionPage = ({ showToast }) => {
  const [activeTab, setActiveTab] = useState('role-matrix'); 
  // 'role-matrix' | 'user-workspaces' | 'user-overrides' | 'effective-inspector' | 'audit-trail'

  // Summary Metrics
  const [summaryStats, setSummaryStats] = useState({
    totalRoles: 0,
    totalUsers: 0,
    totalCentres: 0,
    totalOverrides: 0,
    customAccessUsers: 0,
    totalWorkspaces: 0
  });

  // Lookups & Catalog
  const [catalog, setCatalog] = useState({ modules: [], allActions: [] });
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tab 1: Role Matrix State
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);
  const [originalRolePermissions, setOriginalRolePermissions] = useState([]);
  const [roleMetadata, setRoleMetadata] = useState({ lastModifiedByName: '', lastModifiedDate: '' });
  const [moduleFilter, setModuleFilter] = useState('ALL');
  const [matrixSearch, setMatrixSearch] = useState('');

  // Role Creation Modal State
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [isCreatingRole, setIsCreatingRole] = useState(false);

  // Tab 2: User Center-Role Assignments State
  const [workspaceEmpId, setWorkspaceEmpId] = useState('');
  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [originalWorkspaces, setOriginalWorkspaces] = useState([]);
  const [assignCentreId, setAssignCentreId] = useState('');
  const [assignRoleId, setAssignRoleId] = useState('');
  const [assignIsDefault, setAssignIsDefault] = useState(false);
  const [savingWorkspaces, setSavingWorkspaces] = useState(false);

  // Tab 3: User Overrides State
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [overrideCentreId, setOverrideCentreId] = useState('0'); // '0' = All Centers (Global)
  const [userOverrides, setUserOverrides] = useState([]);
  const [originalUserOverrides, setOriginalUserOverrides] = useState([]);
  const [overrideSearch, setOverrideSearch] = useState('');

  // Tab 4: Effective Permissions Inspector State
  const [inspectorEmployeeId, setInspectorEmployeeId] = useState('');
  const [inspectorRoleId, setInspectorRoleId] = useState('');
  const [inspectorCentreId, setInspectorCentreId] = useState('');
  const [effectiveData, setEffectiveData] = useState(null);
  const [inspectorFilter, setInspectorFilter] = useState('');

  // Tab 5: Audit Trail State
  const [auditTrail, setAuditTrail] = useState([]);
  const [auditFilterType, setAuditFilterType] = useState('ALL');
  const [auditLoading, setAuditLoading] = useState(false);

  // Unsaved Changes Confirmation Modal
  const [pendingTabSwitch, setPendingTabSwitch] = useState(null);

  // Dirty State Computations
  const isRoleMatrixDirty = useMemo(() => {
    if (rolePermissions.length !== originalRolePermissions.length) return true;
    const origMap = new Map(originalRolePermissions.map(p => [`${p.screenId}_${p.actionId}`, !!p.isAllowed]));
    for (const p of rolePermissions) {
      if (origMap.get(`${p.screenId}_${p.actionId}`) !== !!p.isAllowed) return true;
    }
    return false;
  }, [rolePermissions, originalRolePermissions]);

  const isWorkspacesDirty = useMemo(() => {
    if (userWorkspaces.length !== originalWorkspaces.length) return true;
    return JSON.stringify(userWorkspaces) !== JSON.stringify(originalWorkspaces);
  }, [userWorkspaces, originalWorkspaces]);

  const isOverridesDirty = useMemo(() => {
    if (userOverrides.length !== originalUserOverrides.length) return true;
    const origMap = new Map(originalUserOverrides.map(o => [`${o.screenId}_${o.actionId}`, o.overrideType]));
    for (const o of userOverrides) {
      if (origMap.get(`${o.screenId}_${o.actionId}`) !== o.overrideType) return true;
    }
    return false;
  }, [userOverrides, originalUserOverrides]);

  // Load Summary Stats
  const loadSummaryStats = useCallback(async () => {
    try {
      const res = await permissionService.getSummaryStats();
      if (res && res.success && res.data) {
        setSummaryStats(res.data);
      }
    } catch {
      // Non-blocking
    }
  }, []);

  // Initial Load Lookups & Catalog
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, rolesRes, empRes, centreRes] = await Promise.all([
        permissionService.getCatalog(),
        employeeService.getRolesLookup(),
        employeeService.getEmployees({ pageSize: 200 }),
        employeeService.getCentresLookup()
      ]);

      if (catRes && catRes.success && catRes.data) {
        setCatalog(catRes.data);
      }

      let roleList = [];
      if (rolesRes && rolesRes.success && rolesRes.data) {
        const rawRoles = Array.isArray(rolesRes.data) ? rolesRes.data : (rolesRes.data.items || []);
        roleList = rawRoles.map(r => ({
          roleId: r.id || r.roleId,
          roleName: r.name || r.roleName,
          roleCode: r.code || r.roleCode || ''
        })).filter(r => r.roleId != null);
        setRoles(roleList);
        if (roleList.length > 0 && !selectedRoleId) {
          const defaultRole = roleList.find(r => r.roleName?.toLowerCase().includes('technician')) || roleList[0];
          setSelectedRoleId(String(defaultRole.roleId));
          setInspectorRoleId(String(defaultRole.roleId));
          setAssignRoleId(String(defaultRole.roleId));
        }
      }

      let empList = [];
      if (empRes && empRes.success && empRes.data) {
        const raw = empRes.data.items || (Array.isArray(empRes.data) ? empRes.data : []);
        empList = raw.map(e => ({
          employeeId: e.employeeId || e.id || e.employee_ID,
          employeeName: e.name || e.employeeName || e.Name || 'Staff Member',
          employeeCode: e.username || e.employeeCode || e.employee_Code || '',
          designation: e.designation || e.Designation || ''
        })).filter(e => e.employeeId != null);
        setEmployees(empList);
        if (empList.length > 0 && !selectedEmployeeId) {
          const initialEmpId = String(empList[0].employeeId);
          setSelectedEmployeeId(initialEmpId);
          setInspectorEmployeeId(initialEmpId);
          setWorkspaceEmpId(initialEmpId);
        }
      }

      let centreList = [];
      if (centreRes && centreRes.success && centreRes.data) {
        const rawCentres = Array.isArray(centreRes.data) ? centreRes.data : (centreRes.data.items || []);
        centreList = rawCentres.map(c => ({
          centreId: c.id || c.centreId,
          centreName: c.name || c.centreName,
          centreCode: c.code || c.centreCode || ''
        })).filter(c => c.centreId != null);
        setCentres(centreList);
        if (centreList.length > 0 && !inspectorCentreId) {
          const initialCentreId = String(centreList[0].centreId);
          setInspectorCentreId(initialCentreId);
          setAssignCentreId(initialCentreId);
        }
      }

      loadSummaryStats();
    } catch (err) {
      showToast?.('Error loading permission catalog: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, selectedRoleId, selectedEmployeeId, inspectorCentreId, loadSummaryStats]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load Role Permissions (Tab 1)
  const fetchRolePermissions = useCallback(async (roleId) => {
    if (!roleId) return;
    setLoading(true);
    try {
      const res = await permissionService.getRolePermissions(roleId);
      if (res && res.success && res.data) {
        const perms = res.data.permissions || [];
        setRolePermissions(perms);
        setOriginalRolePermissions(JSON.parse(JSON.stringify(perms)));
        setRoleMetadata({
          lastModifiedByName: res.data.lastModifiedByName || 'System Default',
          lastModifiedDate: res.data.lastModifiedDate || ''
        });
      }
    } catch {
      showToast?.('Error loading role permissions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'role-matrix' && selectedRoleId) {
      fetchRolePermissions(selectedRoleId);
    }
  }, [activeTab, selectedRoleId, fetchRolePermissions]);

  // Load User Workspaces (Tab 2)
  const fetchUserWorkspaces = useCallback(async (empId) => {
    if (!empId) return;
    setLoading(true);
    try {
      const res = await permissionService.getUserWorkspaces(empId);
      if (res && res.success && res.data) {
        setUserWorkspaces(res.data || []);
        setOriginalWorkspaces(JSON.parse(JSON.stringify(res.data || [])));
      }
    } catch {
      showToast?.('Error loading user workspaces.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'user-workspaces' && workspaceEmpId) {
      fetchUserWorkspaces(workspaceEmpId);
    }
  }, [activeTab, workspaceEmpId, fetchUserWorkspaces]);

  // Load User Overrides (Tab 3)
  const fetchUserOverrides = useCallback(async (employeeId, centreId = 0) => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await permissionService.getUserOverrides(employeeId, parseInt(centreId, 10));
      if (res && res.success && res.data) {
        const overrides = res.data.overrides || [];
        setUserOverrides(overrides);
        setOriginalUserOverrides(JSON.parse(JSON.stringify(overrides)));
      }
    } catch {
      showToast?.('Error loading user overrides.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (activeTab === 'user-overrides' && selectedEmployeeId) {
      fetchUserOverrides(selectedEmployeeId, overrideCentreId);
    }
  }, [activeTab, selectedEmployeeId, overrideCentreId, fetchUserOverrides]);

  // Load Effective Permissions (Tab 4)
  const fetchEffectivePermissions = useCallback(async () => {
    if (!inspectorEmployeeId || !inspectorRoleId) return;
    setLoading(true);
    try {
      const res = await permissionService.getEffectivePermissions(inspectorEmployeeId, inspectorRoleId, inspectorCentreId ? parseInt(inspectorCentreId, 10) : null);
      if (res && res.success && res.data) {
        setEffectiveData(res.data);
      }
    } catch {
      showToast?.('Error inspecting effective permissions.', 'error');
    } finally {
      setLoading(false);
    }
  }, [inspectorEmployeeId, inspectorRoleId, inspectorCentreId, showToast]);

  useEffect(() => {
    if (activeTab === 'effective-inspector') {
      fetchEffectivePermissions();
    }
  }, [activeTab, fetchEffectivePermissions]);

  // Load Audit Trail (Tab 5)
  const fetchAuditTrail = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = {};
      if (auditFilterType !== 'ALL') params.changeType = auditFilterType;
      const res = await permissionService.getAuditTrail(params);
      if (res && res.success && res.data) {
        setAuditTrail(res.data || []);
      }
    } catch {
      showToast?.('Error loading audit trail.', 'error');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilterType, showToast]);

  useEffect(() => {
    if (activeTab === 'audit-trail') {
      fetchAuditTrail();
    }
  }, [activeTab, fetchAuditTrail]);

  // Handle Tab Switching with Dirty State Warning
  const handleTabClick = (targetTab) => {
    if (targetTab === activeTab) return;
    if (isRoleMatrixDirty || isWorkspacesDirty || isOverridesDirty) {
      setPendingTabSwitch(targetTab);
    } else {
      setActiveTab(targetTab);
    }
  };

  const confirmDiscardAndSwitchTab = () => {
    if (pendingTabSwitch) {
      // Discard pending edits
      setRolePermissions(JSON.parse(JSON.stringify(originalRolePermissions)));
      setUserWorkspaces(JSON.parse(JSON.stringify(originalWorkspaces)));
      setUserOverrides(JSON.parse(JSON.stringify(originalUserOverrides)));
      setActiveTab(pendingTabSwitch);
      setPendingTabSwitch(null);
    }
  };

  // ─── TAB 1: ROLE MATRIX LOGIC ───
  const permissionMap = useMemo(() => {
    const map = new Map();
    rolePermissions.forEach(p => {
      map.set(`${p.screenId}_${p.actionId}`, !!p.isAllowed);
    });
    return map;
  }, [rolePermissions]);

  const toggleRolePermission = (screenId, actionId, screenCode, actionCode) => {
    const key = `${screenId}_${actionId}`;
    const current = permissionMap.get(key) ?? false;
    const next = !current;

    setRolePermissions(prev => {
      const filtered = prev.filter(p => !(p.screenId === screenId && p.actionId === actionId));
      return [...filtered, { screenId, actionId, screenCode, actionCode, isAllowed: next }];
    });
  };

  // Row-level Bulk Toggle
  const toggleAllActionsForScreen = (screen) => {
    const applicable = screen.applicableActions || screen.supportedActions || [];
    if (!applicable.length) return;

    // If all applicable actions are allowed, clear them; otherwise, set all to allowed
    const allAllowed = applicable.every(a => permissionMap.get(`${screen.screenId}_${a.actionId}`));
    const nextState = !allAllowed;

    setRolePermissions(prev => {
      const screenActionIds = new Set(applicable.map(a => a.actionId));
      const filtered = prev.filter(p => !(p.screenId === screen.screenId && screenActionIds.has(p.actionId)));
      const newItems = applicable.map(a => ({
        screenId: screen.screenId,
        actionId: a.actionId,
        screenCode: screen.screenCode,
        actionCode: a.actionCode,
        isAllowed: nextState
      }));
      return [...filtered, ...newItems];
    });
  };

  // Column-level Bulk Toggle
  const toggleAllScreensForAction = (actionId, actionCode) => {
    const relevantScreens = [];
    catalog.modules.forEach(m => {
      (m.screens || []).forEach(s => {
        const app = s.applicableActions || s.supportedActions || [];
        if (app.some(a => a.actionId === actionId)) {
          relevantScreens.push(s);
        }
      });
    });

    if (!relevantScreens.length) return;
    const allAllowed = relevantScreens.every(s => permissionMap.get(`${s.screenId}_${actionId}`));
    const nextState = !allAllowed;

    setRolePermissions(prev => {
      const screenIds = new Set(relevantScreens.map(s => s.screenId));
      const filtered = prev.filter(p => !(screenIds.has(p.screenId) && p.actionId === actionId));
      const newItems = relevantScreens.map(s => ({
        screenId: s.screenId,
        actionId: actionId,
        screenCode: s.screenCode,
        actionCode: actionCode,
        isAllowed: nextState
      }));
      return [...filtered, ...newItems];
    });
  };

  // Select All / Clear All Filtered
  const handleBulkSelectFiltered = (setAllowed) => {
    const updates = [];
    displayedModules.forEach(mod => {
      (mod.screens || []).forEach(screen => {
        if (matchesMatrixSearch(screen)) {
          const app = screen.applicableActions || screen.supportedActions || [];
          app.forEach(a => {
            updates.push({
              screenId: screen.screenId,
              actionId: a.actionId,
              screenCode: screen.screenCode,
              actionCode: a.actionCode,
              isAllowed: setAllowed
            });
          });
        }
      });
    });

    const updateMap = new Map(updates.map(u => [`${u.screenId}_${u.actionId}`, u.isAllowed]));
    setRolePermissions(prev => {
      const filtered = prev.filter(p => !updateMap.has(`${p.screenId}_${p.actionId}`));
      return [...filtered, ...updates];
    });
  };

  const handleSaveRolePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      const payload = rolePermissions.map(p => ({
        screenId: p.screenId,
        actionId: p.actionId,
        isAllowed: !!p.isAllowed
      }));
      const res = await permissionService.saveRolePermissions(selectedRoleId, payload);
      if (res && res.success) {
        showToast?.('Role permissions persisted and audited successfully!', 'success');
        await fetchRolePermissions(selectedRoleId);
        loadSummaryStats();
      } else {
        showToast?.(res?.message || 'Failed to save role permissions.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving role permissions: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardRolePermissions = () => {
    setRolePermissions(JSON.parse(JSON.stringify(originalRolePermissions)));
    showToast?.('Unsaved role permission changes discarded.', 'info');
  };

  // Role Creation
  const handleCreateRoleSubmit = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      showToast?.('Please enter a valid role name.', 'warning');
      return;
    }

    setIsCreatingRole(true);
    try {
      const res = await permissionService.createRole(newRoleName.trim(), newRoleDesc.trim());
      if (res && res.success) {
        showToast?.(`Role "${newRoleName.trim()}" created successfully!`, 'success');
        setIsCreateRoleModalOpen(false);
        setNewRoleName('');
        setNewRoleDesc('');

        const updatedRolesRes = await employeeService.getRolesLookup();
        if (updatedRolesRes && updatedRolesRes.success && updatedRolesRes.data) {
          const updatedList = updatedRolesRes.data.map(r => ({
            roleId: r.id,
            roleName: r.name,
            roleCode: r.code
          }));
          setRoles(updatedList);
          const created = updatedList.find(r => r.roleName.toLowerCase() === newRoleName.trim().toLowerCase());
          if (created) {
            setSelectedRoleId(String(created.roleId));
          }
        }
        loadSummaryStats();
      } else {
        showToast?.(res?.message || 'Failed to create role.', 'error');
      }
    } catch (err) {
      showToast?.('Error creating role: ' + err.message, 'error');
    } finally {
      setIsCreatingRole(false);
    }
  };

  // Filter Modules & Screens
  const matchesMatrixSearch = useCallback((screen) => {
    if (!matrixSearch.trim()) return true;
    const term = matrixSearch.trim().toLowerCase();
    return (
      screen.screenName?.toLowerCase().includes(term) ||
      screen.screenCode?.toLowerCase().includes(term) ||
      screen.routePath?.toLowerCase().includes(term)
    );
  }, [matrixSearch]);

  const displayedModules = useMemo(() => {
    if (!catalog.modules) return [];
    let list = catalog.modules;
    if (moduleFilter !== 'ALL') {
      list = list.filter(m => String(m.moduleId) === String(moduleFilter));
    }
    if (!matrixSearch.trim()) return list;

    return list.map(m => ({
      ...m,
      screens: (m.screens || []).filter(matchesMatrixSearch)
    })).filter(m => m.screens.length > 0);
  }, [catalog.modules, moduleFilter, matrixSearch, matchesMatrixSearch]);

  // ─── TAB 2: USER CENTER-ROLE ASSIGNMENTS LOGIC ───
  const handleAddWorkspace = () => {
    if (!assignCentreId || !assignRoleId) {
      showToast?.('Please select both a Diagnostic Center and a Role.', 'warning');
      return;
    }

    const cId = parseInt(assignCentreId, 10);
    const rId = parseInt(assignRoleId, 10);

    const alreadyExists = userWorkspaces.some(w => w.centreId === cId && w.roleId === rId);
    if (alreadyExists) {
      showToast?.('This staff member is already assigned to this Center and Role.', 'warning');
      return;
    }

    const centreObj = centres.find(c => c.centreId === cId);
    const roleObj = roles.find(r => r.roleId === rId);

    const isFirst = userWorkspaces.length === 0;
    const shouldBeDefault = assignIsDefault || isFirst;

    const updated = userWorkspaces.map(w => ({
      ...w,
      isDefault: shouldBeDefault ? false : w.isDefault
    }));

    updated.push({
      centreId: cId,
      centreName: centreObj?.centreName || 'Center',
      centreCode: centreObj?.centreCode || '',
      roleId: rId,
      roleName: roleObj?.roleName || 'Role',
      isDefault: shouldBeDefault,
      isActive: true
    });

    setUserWorkspaces(updated);
    setAssignIsDefault(false);
    showToast?.('Assignment added. Click "Save User Workspaces" to commit.', 'info');
  };

  const handleRemoveWorkspace = (centreId, roleId) => {
    const remaining = userWorkspaces.filter(w => !(w.centreId === centreId && w.roleId === roleId));
    if (remaining.length > 0 && !remaining.some(w => w.isDefault)) {
      remaining[0].isDefault = true;
    }
    setUserWorkspaces(remaining);
  };

  const handleSetDefaultWorkspace = (centreId, roleId) => {
    const updated = userWorkspaces.map(w => ({
      ...w,
      isDefault: w.centreId === centreId && w.roleId === roleId
    }));
    setUserWorkspaces(updated);
  };

  const handleNavigateToInspectorFromWorkspace = (ws) => {
    setInspectorEmployeeId(String(workspaceEmpId));
    setInspectorCentreId(String(ws.centreId));
    setInspectorRoleId(String(ws.roleId));
    setActiveTab('effective-inspector');
    showToast?.(`Loaded effective rights for ${ws.centreName} as ${ws.roleName}.`, 'info');
  };

  const handleSaveUserWorkspaces = async () => {
    if (!workspaceEmpId) return;
    setSavingWorkspaces(true);
    try {
      const payload = userWorkspaces.map(w => ({
        centreId: w.centreId,
        roleId: w.roleId,
        isDefault: !!w.isDefault
      }));

      const res = await permissionService.saveUserWorkspaces(workspaceEmpId, payload);
      if (res && res.success) {
        showToast?.('User Center-Role assignments saved and audited successfully!', 'success');
        await fetchUserWorkspaces(workspaceEmpId);
        loadSummaryStats();
      } else {
        showToast?.(res?.message || 'Failed to save workspaces.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving user workspaces: ' + err.message, 'error');
    } finally {
      setSavingWorkspaces(false);
    }
  };

  const handleDiscardWorkspaces = () => {
    setUserWorkspaces(JSON.parse(JSON.stringify(originalWorkspaces)));
    showToast?.('Unsaved workspace changes discarded.', 'info');
  };

  // ─── TAB 3: USER OVERRIDES LOGIC ───
  const overrideMap = useMemo(() => {
    const map = new Map();
    userOverrides.forEach(o => {
      map.set(`${o.screenId}_${o.actionId}`, o.overrideType);
    });
    return map;
  }, [userOverrides]);

  const setUserActionOverride = (screenId, actionId, screenCode, actionCode, type) => {
    setUserOverrides(prev => {
      const filtered = prev.filter(o => !(o.screenId === screenId && o.actionId === actionId));
      if (type === 'INHERIT') {
        return [...filtered, { screenId, actionId, screenCode, actionCode, overrideType: 'INHERIT', centreId: parseInt(overrideCentreId, 10) }];
      }
      return [...filtered, { screenId, actionId, screenCode, actionCode, overrideType: type, centreId: parseInt(overrideCentreId, 10) }];
    });
  };

  const handleSaveUserOverrides = async () => {
    if (!selectedEmployeeId) return;
    setSaving(true);
    try {
      const cId = parseInt(overrideCentreId, 10);
      const payload = userOverrides.map(o => ({
        screenId: o.screenId,
        actionId: o.actionId,
        overrideType: o.overrideType,
        centreId: cId
      }));

      const res = await permissionService.saveUserOverrides(selectedEmployeeId, payload, cId);
      if (res && res.success) {
        showToast?.('User permission overrides persisted and audited successfully!', 'success');
        await fetchUserOverrides(selectedEmployeeId, cId);
        loadSummaryStats();
      } else {
        showToast?.(res?.message || 'Failed to save user overrides.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving user overrides: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardUserOverrides = () => {
    setUserOverrides(JSON.parse(JSON.stringify(originalUserOverrides)));
    showToast?.('Unsaved user overrides discarded.', 'info');
  };

  // ─── TAB 4: EFFECTIVE INSPECTOR FILTERING ───
  const filteredEffectiveList = useMemo(() => {
    const list = effectiveData?.permissions || effectiveData?.effectivePermissions || [];
    if (!list.length) return [];
    const term = inspectorFilter.trim().toLowerCase();
    if (!term) return list;
    return list.filter(p =>
      p.screenCode?.toLowerCase().includes(term) ||
      p.screenName?.toLowerCase().includes(term) ||
      p.actionCode?.toLowerCase().includes(term) ||
      p.actionName?.toLowerCase().includes(term) ||
      p.resolutionDetail?.toLowerCase().includes(term)
    );
  }, [effectiveData, inspectorFilter]);

  // Selected Entities Meta for Inspector Flow Banner
  const inspectorUserObj = employees.find(e => String(e.employeeId) === String(inspectorEmployeeId));
  const inspectorRoleObj = roles.find(r => String(r.roleId) === String(inspectorRoleId));
  const inspectorCentreObj = centres.find(c => String(c.centreId) === String(inspectorCentreId));
  const effectiveAllowedCount = (effectiveData?.permissions || []).filter(p => p.isAllowed).length;
  const effectiveTotalCount = (effectiveData?.permissions || []).length;

  // Pagination hooks
  const workspacePagination = usePagination({
    items: userWorkspaces,
    resetDeps: [workspaceEmpId],
  });

  const effectivePagination = usePagination({
    items: filteredEffectiveList,
    resetDeps: [inspectorEmployeeId, inspectorRoleId, inspectorCentreId, inspectorFilter],
  });

  const auditPagination = usePagination({
    items: auditTrail,
    resetDeps: [auditFilterType],
  });

  return (
    <MasterPageWrapper
      title="Role & Access Control"
      subtitle="Manage role permissions, Center assignments, user-specific overrides, and inspect effective rights."
      breadcrumbs={[
        { label: 'Administration', path: '/masters' },
        { label: 'Role & Permissions' }
      ]}
      actions={
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="rbac-btn rbac-btn-outline"
            onClick={() => setIsCreateRoleModalOpen(true)}
            id="btn-create-new-role"
          >
            <span>➕ Create New Role</span>
          </button>
          {activeTab === 'role-matrix' && (
            <>
              {isRoleMatrixDirty && (
                <button
                  type="button"
                  className="rbac-btn rbac-btn-outline"
                  onClick={handleDiscardRolePermissions}
                  disabled={saving || loading}
                >
                  ↩ Discard Changes
                </button>
              )}
              <button
                type="button"
                className="rbac-btn rbac-btn-primary"
                onClick={handleSaveRolePermissions}
                disabled={saving || loading || !selectedRoleId}
                id="btn-save-role-matrix"
              >
                {saving ? 'Saving...' : '💾 Save Role Permissions'}
              </button>
            </>
          )}
          {activeTab === 'user-workspaces' && (
            <>
              {isWorkspacesDirty && (
                <button
                  type="button"
                  className="rbac-btn rbac-btn-outline"
                  onClick={handleDiscardWorkspaces}
                  disabled={savingWorkspaces || loading}
                >
                  ↩ Discard Changes
                </button>
              )}
              <button
                type="button"
                className="rbac-btn rbac-btn-primary"
                onClick={handleSaveUserWorkspaces}
                disabled={savingWorkspaces || loading || !workspaceEmpId}
                id="btn-save-workspaces"
              >
                {savingWorkspaces ? 'Saving...' : '💾 Save User Workspaces'}
              </button>
            </>
          )}
          {activeTab === 'user-overrides' && (
            <>
              {isOverridesDirty && (
                <button
                  type="button"
                  className="rbac-btn rbac-btn-outline"
                  onClick={handleDiscardUserOverrides}
                  disabled={saving || loading}
                >
                  ↩ Discard Changes
                </button>
              )}
              <button
                type="button"
                className="rbac-btn rbac-btn-primary"
                onClick={handleSaveUserOverrides}
                disabled={saving || loading || !selectedEmployeeId}
                id="btn-save-user-overrides"
              >
                {saving ? 'Saving...' : '💾 Save User Overrides'}
              </button>
            </>
          )}
          {activeTab === 'audit-trail' && (
            <button
              type="button"
              className="rbac-btn rbac-btn-outline"
              onClick={fetchAuditTrail}
              disabled={auditLoading}
            >
              🔄 Refresh Audit Trail
            </button>
          )}
        </div>
      }
    >
      <div className="rbac-container">
        {/* Live Summary Stat Cards */}
        <div className="rbac-summary-grid">
          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-blue">🛡️</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">Configured Roles</span>
              <span className="rbac-stat-value">{summaryStats.totalRoles || roles.length}</span>
            </div>
          </div>

          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-emerald">👥</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">Active Users</span>
              <span className="rbac-stat-value">{summaryStats.totalUsers || employees.length}</span>
            </div>
          </div>

          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-purple">🏢</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">Diagnostic Centers</span>
              <span className="rbac-stat-value">{summaryStats.totalCentres || centres.length}</span>
            </div>
          </div>

          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-amber">⚖️</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">User Overrides</span>
              <span className="rbac-stat-value">{summaryStats.totalOverrides}</span>
            </div>
          </div>

          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-rose">👤</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">Custom Access Users</span>
              <span className="rbac-stat-value">{summaryStats.customAccessUsers}</span>
            </div>
          </div>

          <div className="rbac-stat-card">
            <div className="rbac-stat-icon-wrapper rbac-stat-icon-cyan">💼</div>
            <div className="rbac-stat-content">
              <span className="rbac-stat-label">Center Assignments</span>
              <span className="rbac-stat-value">{summaryStats.totalWorkspaces}</span>
            </div>
          </div>
        </div>

        {/* Compact Visual Guide & Permission Legend */}
        <div className="rbac-guide-card">
          <div className="rbac-guide-header">
            <div className="rbac-guide-title">
              <span>💡</span>
              <span>How Permission Evaluation Works</span>
            </div>
            <div className="rbac-legend-pills">
              <span className="rbac-legend-pill legend-allow">✓ ALLOWED</span>
              <span className="rbac-legend-pill legend-deny">✕ DENIED</span>
              <span className="rbac-legend-pill legend-inherit">↔ INHERITED</span>
              <span className="rbac-legend-pill legend-override">⚠ OVERRIDDEN</span>
            </div>
          </div>

          <div className="rbac-flow-steps">
            <div className="rbac-flow-step" title="Defines baseline permissions for all members in role">
              <span className="rbac-flow-step-num">1</span>
              <span>ROLE: Baseline Permissions</span>
            </div>
            <span className="rbac-flow-arrow">➔</span>
            <div className="rbac-flow-step" title="Determines location context and assigned branch role">
              <span className="rbac-flow-step-num">2</span>
              <span>CENTER: Branch Context</span>
            </div>
            <span className="rbac-flow-arrow">➔</span>
            <div className="rbac-flow-step" title="Explicit user-specific overrides take absolute precedence">
              <span className="rbac-flow-step-num">3</span>
              <span>USER OVERRIDE: Specific Exception</span>
            </div>
            <span className="rbac-flow-arrow">➔</span>
            <div className="rbac-flow-step" title="Final effective permission enforced on server and client">
              <span className="rbac-flow-step-num">4</span>
              <span>EFFECTIVE RIGHT: Authoritative Access</span>
            </div>
          </div>
        </div>

        {/* Unsaved Changes Banner */}
        {activeTab === 'role-matrix' && isRoleMatrixDirty && (
          <div className="rbac-dirty-banner">
            <div className="rbac-dirty-text">
              <span>⚠️</span>
              <span>You have unsaved changes in the Role Permissions Matrix. Remember to click Save to persist to the database.</span>
            </div>
            <div className="rbac-dirty-actions">
              <button type="button" className="rbac-btn rbac-btn-outline rbac-btn-sm" onClick={handleDiscardRolePermissions}>
                Discard
              </button>
              <button type="button" className="rbac-btn rbac-btn-primary rbac-btn-sm" onClick={handleSaveRolePermissions} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'user-workspaces' && isWorkspacesDirty && (
          <div className="rbac-dirty-banner">
            <div className="rbac-dirty-text">
              <span>⚠️</span>
              <span>You have unsaved changes in User Workspace Assignments.</span>
            </div>
            <div className="rbac-dirty-actions">
              <button type="button" className="rbac-btn rbac-btn-outline rbac-btn-sm" onClick={handleDiscardWorkspaces}>
                Discard
              </button>
              <button type="button" className="rbac-btn rbac-btn-primary rbac-btn-sm" onClick={handleSaveUserWorkspaces} disabled={savingWorkspaces}>
                {savingWorkspaces ? 'Saving...' : 'Save Assignments'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'user-overrides' && isOverridesDirty && (
          <div className="rbac-dirty-banner">
            <div className="rbac-dirty-text">
              <span>⚠️</span>
              <span>You have unsaved changes in User Overrides for this Center context.</span>
            </div>
            <div className="rbac-dirty-actions">
              <button type="button" className="rbac-btn rbac-btn-outline rbac-btn-sm" onClick={handleDiscardUserOverrides}>
                Discard
              </button>
              <button type="button" className="rbac-btn rbac-btn-primary rbac-btn-sm" onClick={handleSaveUserOverrides} disabled={saving}>
                {saving ? 'Saving...' : 'Save Overrides'}
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs Bar */}
        <div className="rbac-tabs-bar">
          <button
            type="button"
            className={`rbac-tab-btn ${activeTab === 'role-matrix' ? 'active' : ''}`}
            onClick={() => handleTabClick('role-matrix')}
            id="tab-role-matrix"
          >
            <span className="rbac-tab-icon">🛡️</span>
            <span>Role Permissions Matrix</span>
            {isRoleMatrixDirty && <span className="rbac-tab-badge">●</span>}
          </button>

          <button
            type="button"
            className={`rbac-tab-btn ${activeTab === 'user-workspaces' ? 'active' : ''}`}
            onClick={() => handleTabClick('user-workspaces')}
            id="tab-user-workspaces"
          >
            <span className="rbac-tab-icon">🏢</span>
            <span>User Center-Role Assignments</span>
            {isWorkspacesDirty && <span className="rbac-tab-badge">●</span>}
          </button>

          <button
            type="button"
            className={`rbac-tab-btn ${activeTab === 'user-overrides' ? 'active' : ''}`}
            onClick={() => handleTabClick('user-overrides')}
            id="tab-user-overrides"
          >
            <span className="rbac-tab-icon">👤</span>
            <span>User-Level Overrides</span>
            {isOverridesDirty && <span className="rbac-tab-badge">●</span>}
          </button>

          <button
            type="button"
            className={`rbac-tab-btn ${activeTab === 'effective-inspector' ? 'active' : ''}`}
            onClick={() => handleTabClick('effective-inspector')}
            id="tab-effective-inspector"
          >
            <span className="rbac-tab-icon">🔍</span>
            <span>Effective Rights Inspector</span>
          </button>

          <button
            type="button"
            className={`rbac-tab-btn ${activeTab === 'audit-trail' ? 'active' : ''}`}
            onClick={() => handleTabClick('audit-trail')}
            id="tab-audit-trail"
          >
            <span className="rbac-tab-icon">📜</span>
            <span>Audit Trail & Security Logs</span>
          </button>
        </div>

        {/* ─── TAB 1: ROLE PERMISSION MATRIX ─── */}
        {activeTab === 'role-matrix' && (
          <div className="rbac-tab-content">
            <div className="rbac-toolbar-card">
              <div className="rbac-toolbar-left">
                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="role-select">Select Target Role:</label>
                  <select
                    id="role-select"
                    className="rbac-select"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    disabled={loading || saving}
                  >
                    {roles.length === 0 ? (
                      <option value="">No roles available</option>
                    ) : (
                      roles.map(r => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.roleName} ({r.roleCode})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="module-filter">Filter Module:</label>
                  <select
                    id="module-filter"
                    className="rbac-select"
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                  >
                    <option value="ALL">All Application Modules</option>
                    {catalog.modules.map(m => (
                      <option key={m.moduleId} value={m.moduleId}>
                        {m.moduleName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="matrix-search">Search Feature:</label>
                  <input
                    id="matrix-search"
                    type="text"
                    className="rbac-input"
                    placeholder="Search screen name, code..."
                    value={matrixSearch}
                    onChange={(e) => setMatrixSearch(e.target.value)}
                    style={{ minWidth: 200 }}
                  />
                </div>
              </div>

              <div className="rbac-toolbar-right">
                <button
                  type="button"
                  className="rbac-btn rbac-btn-outline rbac-btn-sm"
                  onClick={() => handleBulkSelectFiltered(true)}
                  title="Select all actions for visible features"
                >
                  ☑️ Select All Visible
                </button>
                <button
                  type="button"
                  className="rbac-btn rbac-btn-outline rbac-btn-sm"
                  onClick={() => handleBulkSelectFiltered(false)}
                  title="Clear all actions for visible features"
                >
                  ⬜ Clear All Visible
                </button>
                <div className="rbac-stat-content" style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {roleMetadata.lastModifiedByName ? (
                      <>Updated by <strong>{roleMetadata.lastModifiedByName}</strong> {roleMetadata.lastModifiedDate ? `on ${new Date(roleMetadata.lastModifiedDate).toLocaleDateString()}` : ''}</>
                    ) : 'System Baseline'}
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#10b981' }}>
                    {rolePermissions.filter(p => p.isAllowed).length} Actions Allowed
                  </span>
                </div>
              </div>
            </div>

            {/* Matrix Grid Table */}
            <div className="rbac-matrix-card">
              <div className="rbac-table-container">
                <table className="rbac-matrix-table">
                  <thead>
                    <tr>
                      <th className="th-screen">Screen / Feature</th>
                      {catalog.allActions.map(action => (
                        <th key={action.actionId} className="th-action text-center">
                          <div className="th-action-header">
                            <span>{action.actionName}</span>
                            <button
                              type="button"
                              className="th-col-toggle"
                              onClick={() => toggleAllScreensForAction(action.actionId, action.actionCode)}
                              title={`Toggle all ${action.actionName} permissions`}
                            >
                              Toggle Col
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedModules.length === 0 ? (
                      <tr>
                        <td colSpan={catalog.allActions.length + 1} style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                          No screens match the selected module and search filters.
                        </td>
                      </tr>
                    ) : (
                      displayedModules.map(module => (
                        <React.Fragment key={module.moduleId}>
                          <tr className="rbac-module-header-row">
                            <td colSpan={catalog.allActions.length + 1}>
                              <span className="rbac-module-badge">{module.moduleName}</span>
                            </td>
                          </tr>
                          {module.screens.map(screen => (
                            <tr key={screen.screenId} className="rbac-screen-row">
                              <td className="td-screen-name">
                                <div className="rbac-screen-row-header">
                                  <div>
                                    <div className="rbac-screen-title">{screen.screenName}</div>
                                    <div className="rbac-screen-code">{screen.screenCode} • {screen.routePath}</div>
                                  </div>
                                  <button
                                    type="button"
                                    className="rbac-row-toggle-btn"
                                    onClick={() => toggleAllActionsForScreen(screen)}
                                    title="Toggle all applicable actions for this screen"
                                  >
                                    Toggle Row
                                  </button>
                                </div>
                              </td>
                              {catalog.allActions.map(action => {
                                const supported = screen.applicableActions || screen.supportedActions || [];
                                const isSupported = supported.some(a => a.actionId === action.actionId);
                                if (!isSupported) {
                                  return (
                                    <td key={action.actionId} className="text-center td-not-applicable">
                                      <span className="rbac-na-dot">—</span>
                                    </td>
                                  );
                                }

                                const isAllowed = permissionMap.get(`${screen.screenId}_${action.actionId}`) ?? false;

                                return (
                                  <td key={action.actionId} className="text-center td-checkbox-cell">
                                    <label className="rbac-checkbox-wrapper" title={`${screen.screenName} → ${action.actionName}: ${isAllowed ? 'Allowed' : 'Not Allowed'}`}>
                                      <input
                                        type="checkbox"
                                        checked={isAllowed}
                                        onChange={() => toggleRolePermission(screen.screenId, action.actionId, screen.screenCode, action.actionCode)}
                                      />
                                      <span className="rbac-custom-checkbox"></span>
                                    </label>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 2: USER CENTER-ROLE ASSIGNMENTS ─── */}
        {activeTab === 'user-workspaces' && (
          <div className="rbac-tab-content">
            <div className="rbac-toolbar-card">
              <div className="rbac-filter-inline">
                <label className="rbac-filter-label" htmlFor="ws-emp-select">Select Employee / Staff Member:</label>
                <select
                  id="ws-emp-select"
                  className="rbac-select"
                  value={workspaceEmpId}
                  onChange={(e) => setWorkspaceEmpId(e.target.value)}
                  style={{ minWidth: 280 }}
                >
                  {employees.map(emp => (
                    <option key={emp.employeeId} value={emp.employeeId}>
                      {emp.employeeName} ({emp.employeeCode || 'ID: ' + emp.employeeId}) — {emp.designation || 'Staff'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rbac-stat-content" style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2563eb' }}>
                  {userWorkspaces.length} Authorized Center Workspaces
                </span>
              </div>
            </div>

            {/* Current Assignments Table */}
            <div className="rbac-matrix-card" style={{ padding: '1.25rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '1rem' }}>
                🏢 Currently Authorized Workspaces for Selected Staff Member
              </h3>

              <div className="rbac-table-container" style={{ marginBottom: '1.5rem' }}>
                <table className="rbac-matrix-table">
                  <thead>
                    <tr>
                      <th>Diagnostic Center / Branch</th>
                      <th>Center Code</th>
                      <th>Assigned Role in Center</th>
                      <th className="text-center">Primary (Default)</th>
                      <th className="text-center">Effective Rights</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspacePagination.totalRecords === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                          No workspaces assigned to this employee yet. Use the form below to authorize centers and roles.
                        </td>
                      </tr>
                    ) : (
                      workspacePagination.paginatedItems.map((ws, idx) => (
                        <tr key={idx}>
                          <td><strong>{ws.centreName}</strong></td>
                          <td><code>{ws.centreCode}</code></td>
                          <td>
                            <span className="rbac-action-badge">{ws.roleName}</span>
                          </td>
                          <td className="text-center">
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name="defaultWorkspaceRadio"
                                checked={!!ws.isDefault}
                                onChange={() => handleSetDefaultWorkspace(ws.centreId, ws.roleId)}
                              />
                              <span style={{ fontSize: '0.85rem', fontWeight: ws.isDefault ? 700 : 400, color: ws.isDefault ? '#10b981' : '#64748b' }}>
                                {ws.isDefault ? '✓ Default (Primary)' : 'Make Default'}
                              </span>
                            </label>
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="rbac-btn rbac-btn-outline rbac-btn-sm"
                              onClick={() => handleNavigateToInspectorFromWorkspace(ws)}
                              title="Inspect what permissions this user gets in this center"
                            >
                              🔍 View Rights
                            </button>
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="rbac-btn rbac-btn-danger-outline rbac-btn-sm"
                              onClick={() => handleRemoveWorkspace(ws.centreId, ws.roleId)}
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Centralized Pagination */}
              <CommonPagination
                page={workspacePagination.page}
                pageSize={workspacePagination.pageSize}
                totalRecords={workspacePagination.totalRecords}
                onPageChange={workspacePagination.setPage}
                onPageSizeChange={workspacePagination.setPageSize}
                loading={loading}
              />

              {/* Add New Center-Role Assignment Form */}
              <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
                  ➕ Authorize Additional Diagnostic Center & Role
                </h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div className="rbac-field">
                    <label className="rbac-filter-label" htmlFor="new-ws-centre">Diagnostic Center:</label>
                    <select
                      id="new-ws-centre"
                      className="rbac-select"
                      value={assignCentreId}
                      onChange={(e) => setAssignCentreId(e.target.value)}
                    >
                      {centres.map(c => (
                        <option key={c.centreId} value={c.centreId}>
                          {c.centreName} ({c.centreCode})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rbac-field">
                    <label className="rbac-filter-label" htmlFor="new-ws-role">Assigned Role:</label>
                    <select
                      id="new-ws-role"
                      className="rbac-select"
                      value={assignRoleId}
                      onChange={(e) => setAssignRoleId(e.target.value)}
                    >
                      {roles.map(r => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.roleName} ({r.roleCode})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rbac-field">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.85rem' }}>
                      <input
                        type="checkbox"
                        checked={assignIsDefault}
                        onChange={(e) => setAssignIsDefault(e.target.checked)}
                      />
                      <span>Set as Default Workspace</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    className="rbac-btn rbac-btn-primary"
                    onClick={handleAddWorkspace}
                    id="btn-add-workspace-row"
                  >
                    ➕ Add Assignment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 3: USER PERMISSION OVERRIDES ─── */}
        {activeTab === 'user-overrides' && (
          <div className="rbac-tab-content">
            <div className="rbac-toolbar-card">
              <div className="rbac-toolbar-left">
                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="emp-select">Select Employee:</label>
                  <select
                    id="emp-select"
                    className="rbac-select"
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    style={{ minWidth: 260 }}
                  >
                    {employees.map(emp => (
                      <option key={emp.employeeId} value={emp.employeeId}>
                        {emp.employeeName} ({emp.employeeCode || 'ID: ' + emp.employeeId})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="override-centre-select">Center Scope:</label>
                  <select
                    id="override-centre-select"
                    className="rbac-select"
                    value={overrideCentreId}
                    onChange={(e) => setOverrideCentreId(e.target.value)}
                    style={{ minWidth: 240 }}
                  >
                    <option value="0">🌐 All Centers (Global User Override)</option>
                    {centres.map(c => (
                      <option key={c.centreId} value={c.centreId}>
                        🏢 {c.centreName} ({c.centreCode})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-filter-inline">
                  <label className="rbac-filter-label" htmlFor="override-search">Filter Feature:</label>
                  <input
                    id="override-search"
                    type="text"
                    className="rbac-input"
                    placeholder="Search screen or action..."
                    value={overrideSearch}
                    onChange={(e) => setOverrideSearch(e.target.value)}
                    style={{ minWidth: 180 }}
                  />
                </div>
              </div>

              <div className="rbac-stat-content" style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f59e0b' }}>
                  {userOverrides.filter(o => o.overrideType !== 'INHERIT').length} Active Overrides
                </span>
              </div>
            </div>

            {/* Overrides Table */}
            <div className="rbac-matrix-card">
              <div className="rbac-table-container">
                <table className="rbac-matrix-table">
                  <thead>
                    <tr>
                      <th>Screen Name & Code</th>
                      <th>Action</th>
                      <th>Explicit Override Choice</th>
                      <th className="text-center">Effective State Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.modules.map(module => {
                      const screensWithActions = (module.screens || []).filter(s => {
                        if (!overrideSearch.trim()) return true;
                        const term = overrideSearch.trim().toLowerCase();
                        return (
                          s.screenName?.toLowerCase().includes(term) ||
                          s.screenCode?.toLowerCase().includes(term)
                        );
                      });

                      if (!screensWithActions.length) return null;

                      return (
                        <React.Fragment key={module.moduleId}>
                          <tr className="rbac-module-header-row">
                            <td colSpan={4}>
                              <span className="rbac-module-badge">{module.moduleName}</span>
                            </td>
                          </tr>
                          {screensWithActions.map(screen => {
                            const supported = screen.applicableActions || screen.supportedActions || [];
                            return supported.map(action => {
                              const key = `${screen.screenId}_${action.actionId}`;
                              const currentType = overrideMap.get(key) || 'INHERIT';

                              return (
                                <tr key={key} className="rbac-override-row">
                                  <td className="td-screen-name">
                                    <strong>{screen.screenName}</strong>
                                    <span className="text-muted" style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
                                      ({screen.screenCode})
                                    </span>
                                  </td>
                                  <td>
                                    <span className="rbac-action-badge">{action.actionName}</span>
                                  </td>
                                  <td>
                                    <div className="rbac-tristate-group">
                                      <button
                                        type="button"
                                        className={`rbac-tristate-btn ${currentType === 'INHERIT' ? 'active-inherit' : ''}`}
                                        onClick={() => setUserActionOverride(screen.screenId, action.actionId, screen.screenCode, action.actionCode, 'INHERIT')}
                                        title="Defer to role permission"
                                      >
                                        ↔ Inherit
                                      </button>
                                      <button
                                        type="button"
                                        className={`rbac-tristate-btn ${currentType === 'ALLOW' ? 'active-allow' : ''}`}
                                        onClick={() => setUserActionOverride(screen.screenId, action.actionId, screen.screenCode, action.actionCode, 'ALLOW')}
                                        title="Explicitly allow this action"
                                      >
                                        ✓ Allow
                                      </button>
                                      <button
                                        type="button"
                                        className={`rbac-tristate-btn ${currentType === 'DENY' ? 'active-deny' : ''}`}
                                        onClick={() => setUserActionOverride(screen.screenId, action.actionId, screen.screenCode, action.actionCode, 'DENY')}
                                        title="Explicitly deny this action"
                                      >
                                        ✕ Deny
                                      </button>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    {currentType === 'ALLOW' && (
                                      <span className="rbac-badge rbac-badge-allowed">✓ OVERRIDDEN: ALLOW</span>
                                    )}
                                    {currentType === 'DENY' && (
                                      <span className="rbac-badge rbac-badge-denied">✕ OVERRIDDEN: DENY</span>
                                    )}
                                    {currentType === 'INHERIT' && (
                                      <span className="rbac-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>
                                        ↔ INHERITED FROM ROLE
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB 4: EFFECTIVE PERMISSIONS INSPECTOR ─── */}
        {activeTab === 'effective-inspector' && (
          <div className="rbac-tab-content">
            {/* Visual Resolution Pipeline Header */}
            <div className="rbac-inspector-flow">
              <div className="rbac-flow-node rbac-node-user">
                <span className="rbac-node-icon">👤</span>
                <span className="rbac-node-label">Inspected User</span>
                <span className="rbac-node-val">{inspectorUserObj?.employeeName || 'Selected User'}</span>
              </div>

              <span className="rbac-flow-node-arrow">➔</span>

              <div className="rbac-flow-node rbac-node-centre">
                <span className="rbac-node-icon">🏢</span>
                <span className="rbac-node-label">Center Context</span>
                <span className="rbac-node-val">{inspectorCentreObj?.centreName || 'Selected Center'}</span>
              </div>

              <span className="rbac-flow-node-arrow">➔</span>

              <div className="rbac-flow-node rbac-node-role">
                <span className="rbac-node-icon">🛡️</span>
                <span className="rbac-node-label">Active Role</span>
                <span className="rbac-node-val">{inspectorRoleObj?.roleName || 'Selected Role'}</span>
              </div>

              <span className="rbac-flow-node-arrow">➔</span>

              <div className="rbac-flow-node rbac-node-override">
                <span className="rbac-node-icon">⚖️</span>
                <span className="rbac-node-label">Overrides Evaluated</span>
                <span className="rbac-node-val">Center & Global</span>
              </div>

              <span className="rbac-flow-node-arrow">➔</span>

              <div className="rbac-flow-node rbac-node-effective">
                <span className="rbac-node-icon">🎯</span>
                <span className="rbac-node-label">Final Access</span>
                <span className="rbac-node-val" style={{ color: '#10b981' }}>
                  {effectiveAllowedCount} / {effectiveTotalCount} Allowed
                </span>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="rbac-toolbar-card">
              <div className="rbac-filter-grid">
                <div className="rbac-field">
                  <label className="rbac-filter-label" htmlFor="insp-emp-select">Employee:</label>
                  <select
                    id="insp-emp-select"
                    className="rbac-select"
                    value={inspectorEmployeeId}
                    onChange={(e) => setInspectorEmployeeId(e.target.value)}
                  >
                    {employees.map(emp => (
                      <option key={emp.employeeId} value={emp.employeeId}>
                        {emp.employeeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-field">
                  <label className="rbac-filter-label" htmlFor="insp-centre-select">Center Scope:</label>
                  <select
                    id="insp-centre-select"
                    className="rbac-select"
                    value={inspectorCentreId}
                    onChange={(e) => setInspectorCentreId(e.target.value)}
                  >
                    {centres.map(c => (
                      <option key={c.centreId} value={c.centreId}>
                        {c.centreName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-field">
                  <label className="rbac-filter-label" htmlFor="insp-role-select">Active Role:</label>
                  <select
                    id="insp-role-select"
                    className="rbac-select"
                    value={inspectorRoleId}
                    onChange={(e) => setInspectorRoleId(e.target.value)}
                  >
                    {roles.map(r => (
                      <option key={r.roleId} value={r.roleId}>
                        {r.roleName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rbac-field" style={{ flex: 1 }}>
                  <label className="rbac-filter-label" htmlFor="insp-search-input">Search Right:</label>
                  <input
                    id="insp-search-input"
                    type="text"
                    className="rbac-input"
                    placeholder="Search by screen code, name, action..."
                    value={inspectorFilter}
                    onChange={(e) => setInspectorFilter(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Inspector Table */}
            <div className="rbac-matrix-card">
              <div className="rbac-table-container">
                <table className="rbac-matrix-table">
                  <thead>
                    <tr>
                      <th>Screen / Feature</th>
                      <th>Screen Code</th>
                      <th>Action</th>
                      <th className="text-center">Effective Permission</th>
                      <th>Precedence Resolution Source</th>
                      <th>Audit & Security Detail (WHY)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectivePagination.totalRecords === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                          No permission records match the inspection criteria.
                        </td>
                      </tr>
                    ) : (
                      effectivePagination.paginatedItems.map((p, idx) => {
                        let sourceClass = 'source-default-deny';
                        let sourceLabel = '🔒 Default Deny';
                        if (p.source === 'USER_OVERRIDE') {
                          sourceClass = p.resolutionDetail?.includes('Center User Override')
                            ? 'source-centre-user-override'
                            : 'source-global-user-override';
                          sourceLabel = p.resolutionDetail?.includes('Center User Override')
                            ? '👤 Center User Override'
                            : '🌐 Global User Override';
                        } else if (p.source === 'CENTRE_ROLE') {
                          sourceClass = 'source-centre-role';
                          sourceLabel = '🏢 Center Role';
                        } else if (p.source === 'ROLE') {
                          sourceClass = p.resolutionDetail?.includes('Administrator')
                            ? 'source-admin-bypass'
                            : 'source-role';
                          sourceLabel = p.resolutionDetail?.includes('Administrator')
                            ? '👑 Administrator Bypass'
                            : '🛡️ Role Permission';
                        }

                        return (
                          <tr key={idx}>
                            <td><strong>{p.screenName}</strong></td>
                            <td><code>{p.screenCode}</code></td>
                            <td><span className="rbac-action-badge">{p.actionName}</span></td>
                            <td className="text-center">
                              {p.isAllowed ? (
                                <span className="rbac-badge rbac-badge-allowed">✓ ALLOWED</span>
                              ) : (
                                <span className="rbac-badge rbac-badge-denied">✕ DENIED</span>
                              )}
                            </td>
                            <td>
                              <span className={`rbac-source-tag ${sourceClass}`}>
                                {sourceLabel}
                              </span>
                            </td>
                            <td>
                              <span className="rbac-resolution-text">
                                {p.resolutionDetail || (p.isAllowed ? 'Allowed by authorization policy' : 'Denied by default')}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Centralized Pagination */}
              <CommonPagination
                page={effectivePagination.page}
                pageSize={effectivePagination.pageSize}
                totalRecords={effectivePagination.totalRecords}
                onPageChange={effectivePagination.setPage}
                onPageSizeChange={effectivePagination.setPageSize}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* ─── TAB 5: AUDIT TRAIL & SECURITY LOGS ─── */}
        {activeTab === 'audit-trail' && (
          <div className="rbac-tab-content">
            <div className="rbac-toolbar-card">
              <div className="rbac-filter-inline">
                <label className="rbac-filter-label" htmlFor="audit-filter">Filter Event Type:</label>
                <select
                  id="audit-filter"
                  className="rbac-select"
                  value={auditFilterType}
                  onChange={(e) => setAuditFilterType(e.target.value)}
                >
                  <option value="ALL">All RBAC Events</option>
                  <option value="ROLE_PERM_UPDATE">Role Permission Updates</option>
                  <option value="USER_OVERRIDE_UPSERT">User Override Added/Updated</option>
                  <option value="USER_OVERRIDE_DELETE">User Override Deleted (Inherited)</option>
                  <option value="WORKSPACE_ASSIGNMENT">Center Workspace Assignments</option>
                </select>
              </div>

              <div className="rbac-stat-content" style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b21a8' }}>
                  {auditTrail.length} Logged Audit Events
                </span>
              </div>
            </div>

            <div className="rbac-matrix-card">
              <div className="rbac-table-container">
                <table className="rbac-matrix-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Change Type</th>
                      <th>Target Subject</th>
                      <th>Center Scope</th>
                      <th>Feature & Action</th>
                      <th>Previous ➔ New Value</th>
                      <th>Performed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditPagination.totalRecords === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                          No audit records found for the selected filter.
                        </td>
                      </tr>
                    ) : (
                      auditPagination.paginatedItems.map((a, idx) => (
                        <tr key={idx}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: '#475569' }}>
                            {a.createdDate ? new Date(a.createdDate).toLocaleString() : '—'}
                          </td>
                          <td>
                            <span className="rbac-action-badge">
                              {a.changeType}
                            </span>
                          </td>
                          <td>
                            <strong>{a.roleName ? `Role: ${a.roleName}` : a.employeeName ? `User: ${a.employeeName}` : 'System'}</strong>
                          </td>
                          <td>
                            {a.centreName ? a.centreName : <span style={{ color: '#94a3b8' }}>Global (All Centers)</span>}
                          </td>
                          <td>
                            {a.screenName || a.actionName ? (
                              <span>{a.screenName || a.screenCode} • <strong>{a.actionName || a.actionCode}</strong></span>
                            ) : '—'}
                          </td>
                          <td>
                            <span style={{ fontSize: '0.825rem', fontFamily: 'monospace' }}>
                              {a.previousValue || 'None'} ➔ <strong>{a.newValue || 'None'}</strong>
                            </span>
                          </td>
                          <td>
                            <strong>{a.modifiedByName || 'System'}</strong>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <CommonPagination
                page={auditPagination.page}
                pageSize={auditPagination.pageSize}
                totalRecords={auditPagination.totalRecords}
                onPageChange={auditPagination.setPage}
                onPageSizeChange={auditPagination.setPageSize}
                loading={auditLoading}
              />
            </div>
          </div>
        )}

        {/* Modal: Create New Role */}
        {isCreateRoleModalOpen && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div className="rbac-modal-overlay">
            <div className="rbac-modal-box" style={{ maxWidth: 480 }}>
              <div className="rbac-modal-header">
                <h3 className="rbac-modal-title">➕ Create New Custom Role</h3>
                <button
                  type="button"
                  className="rbac-modal-close"
                  onClick={() => setIsCreateRoleModalOpen(false)}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreateRoleSubmit} className="rbac-modal-body">
                <div style={{ marginBottom: '1rem' }}>
                  <label className="rbac-filter-label" style={{ display: 'block', marginBottom: '0.4rem' }}>
                    Role Name *
                  </label>
                  <input
                    type="text"
                    className="rbac-input"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="e.g. Senior Microbiologist, Lead Phlebotomist"
                    required
                    autoFocus
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="rbac-filter-label" style={{ display: 'block', marginBottom: '0.4rem' }}>
                    Description / Authorization Scope
                  </label>
                  <textarea
                    className="rbac-input"
                    rows={3}
                    value={newRoleDesc}
                    onChange={(e) => setNewRoleDesc(e.target.value)}
                    placeholder="Brief description of clinical responsibilities and access boundary..."
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="rbac-btn rbac-btn-outline"
                    onClick={() => setIsCreateRoleModalOpen(false)}
                    disabled={isCreatingRole}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rbac-btn rbac-btn-primary"
                    disabled={isCreatingRole}
                  >
                    {isCreatingRole ? 'Creating...' : 'Create Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Modal: Confirm Tab Switch with Unsaved Changes */}
        {pendingTabSwitch && typeof document !== 'undefined' && ReactDOM.createPortal(
          <div className="rbac-modal-overlay">
            <div className="rbac-modal-box" style={{ maxWidth: 440 }}>
              <div className="rbac-modal-header">
                <h3 className="rbac-modal-title" style={{ color: '#b91c1c' }}>⚠️ Unsaved Changes</h3>
                <button
                  type="button"
                  className="rbac-modal-close"
                  onClick={() => setPendingTabSwitch(null)}
                >
                  ✕
                </button>
              </div>

              <div className="rbac-modal-body">
                <p style={{ color: '#334155', fontSize: '0.9rem', lineHeight: 1.5, margin: '0 0 1.25rem 0' }}>
                  You have pending changes that have not been saved to the database. If you switch tabs now, your unsaved changes will be discarded.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="rbac-btn rbac-btn-outline"
                    onClick={() => setPendingTabSwitch(null)}
                  >
                    Keep Editing
                  </button>
                  <button
                    type="button"
                    className="rbac-btn rbac-btn-primary"
                    style={{ background: '#dc2626' }}
                    onClick={confirmDiscardAndSwitchTab}
                  >
                    Discard & Continue
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </MasterPageWrapper>
  );
};

export default RolePermissionPage;
